import { getLogger } from 'cadenza/utils/logging';
import { isFeatureAvailable } from 'cadenza/features';
import { assertNonNullable } from 'cadenza/utils/custom-error';
import type { SelectionEventDetail } from 'cadenza/integration/cadenza-api-selection-util';

/* eslint-disable @typescript-eslint/no-restricted-imports */
import type {
  CadenzaEvent,
  CadenzaEventByType as PublicCadenzaEventByType,
  CadenzaEventType as PublicCadenzaEventType,
  CustomValidityType,
  Extent,
  FilterVariables,
  GeometryExtentStrategy,
  WorkbookLayerPath,
} from '@disy/cadenza.js';
import type { Geometry } from 'geojson';
/* eslint-enable @typescript-eslint/no-restricted-imports */

const logger = getLogger('cadenza/integration/post-message');

if (isFeatureAvailable('CADENZA_JS_SANDBOX')) {
  logger.enableAll();
}

type OutgoingCadenzaEventType = PublicCadenzaEventType | 'ready' | 'change:extent';
type OutgoingCadenzaEvent<T extends OutgoingCadenzaEventType> =
  T extends PublicCadenzaEventType ? PublicCadenzaEventByType<T>
  : T extends 'change:extent' ? CadenzaEvent<T, { extent: Extent }>
  : never;

type IncomingCadenzaEventType =
  | 'expandNavigator'
  | 'getData'
  | 'setCustomValidity'
  | 'setGeometry'
  | 'setExtentStrategy'
  | 'setFilter'
  | 'setLayerVisibility'
  | 'setSelection'
  | 'addSelection'
  | 'removeSelection'
  | 'reload'
  | 'closeMe';

export type IncomingCadenzaEvent<T extends IncomingCadenzaEventType = IncomingCadenzaEventType> = {
  responsePort?: MessagePort;
} & (T extends 'expandNavigator' ? CadenzaEvent<T, { expandNavigator: boolean }>
: T extends 'getData' ? CadenzaEvent<T, never>
: T extends 'setCustomValidity' ? CadenzaEvent<T, { message: string; type: CustomValidityType }>
: T extends 'setGeometry' ? CadenzaEvent<T, { geometry: Geometry; zoomToGeometry?: boolean }>
: T extends 'setFilter' ? CadenzaEvent<T, { filter: FilterVariables }>
: T extends 'setLayerVisibility' ? CadenzaEvent<T, { layer: WorkbookLayerPath; visible: boolean }>
: T extends 'setSelection' ? CadenzaEvent<T, SelectionEventDetail>
: T extends 'addSelection' ? CadenzaEvent<T, SelectionEventDetail>
: T extends 'removeSelection' ? CadenzaEvent<T, SelectionEventDetail>
: T extends 'reload' ? CadenzaEvent<T, { invalidateCaches: boolean }>
: T extends 'setExtentStrategy' ? CadenzaEvent<T, GeometryExtentStrategy>
: never);

type Subscriber<T extends IncomingCadenzaEventType = never> = (event: IncomingCadenzaEvent<T>) => void;

interface Subscription {
  type: IncomingCadenzaEventType;
  sourceWindow?: Window;
  subscriber: Subscriber<IncomingCadenzaEventType>;
}

const subscriptions: Subscription[] = [];
const parentTargetOrigin = getParentTargetOrigin();

/**
 * Subscribe to events from the specified window.
 *
 * @param type - The event type
 * @param subscriber - The subscriber function
 * @param [sourceWindow] - The window in which to listen for the event; current window if
 *   unspecified.
 * @return An unsubscribe function
 */
export function subscribeToEvent<T extends IncomingCadenzaEventType>(
  type: T,
  subscriber: Subscriber<T>,
  sourceWindow?: Window,
): () => void {
  if (subscriptions.length === 0) {
    window.addEventListener('message', __onMessage__);
  }
  subscriptions.push({ type, sourceWindow, subscriber: subscriber as Subscriber<IncomingCadenzaEventType> });
  return () => {
    subscriptions.forEach(
      ({ type: subscriptionType, sourceWindow: subscriptionSourceWindow, subscriber: subscriptionSubscriber }, i) => {
        if (
          subscriptionType === type &&
          subscriptionSourceWindow === sourceWindow &&
          subscriptionSubscriber === subscriber
        ) {
          subscriptions.splice(i, 1);
        }
      },
    );
    if (subscriptions.length === 0) {
      window.removeEventListener('message', __onMessage__);
    }
  };
}

// Exported for testing
export function __onMessage__(event: MessageEvent<IncomingCadenzaEvent>) {
  if (window.Disy.targetOrigin !== '*' && location.origin !== event.origin && !isKnownSourceOrigin(event.origin)) {
    return;
  }

  logger.log('Received message', event);

  const cadenzaEvent = event.data;
  cadenzaEvent.responsePort = event.ports[0];
  subscriptions.forEach(({ type, sourceWindow, subscriber }) => {
    if (type === cadenzaEvent.type && (!sourceWindow || sourceWindow === event.source)) {
      subscriber(cadenzaEvent);
    }
  });
}

export function isKnownSourceOrigin(url: string) {
  // try-catch-block can be replaced with URL.canParse when browser/JsDOM support is better
  try {
    return window.Disy.knownSourceOrigins.includes(new URL(url).origin);
  } catch (e) {
    // @ts-expect-error Cannot check for "e instance of TypeError" because that returns false with
    // jsdom, i.e. in Jest tests, see https://github.com/jsdom/jsdom/issues/3082
    if (e.name === 'TypeError') {
      // url cannot be parsed
      return false;
    } else {
      throw e;
    }
  }
}

/**
 * Post an event to the target window.
 *
 * The event is sent using `postMessage()` to the target window.
 *
 * If no target is specified, the event is posted to the `parent` window (assuming that Cadenza is
 * embedded) and the targetOrigin is calculated automatically, too. By default, the
 * `location.origin` is used as the target origin. If a `webApplication` URL parameter with the ID
 * of an external link is present, the origin is taken from the external link.
 *
 * @param type - The event type
 * @param [detail] - The event detail
 * @param [options]
 * @param [options.targetWindow] - the target window for the message
 * @param [options.targetOrigin] - the target origin for the message
 */
export async function postEvent<T extends OutgoingCadenzaEventType>(
  type: T,
  detail?: OutgoingCadenzaEvent<T>['detail'],
  options: { targetWindow?: Window; targetOrigin?: string } = {},
) {
  const { targetWindow = window.parent, targetOrigin = parentTargetOrigin } = options;
  const event = { type, detail };
  logger.log(`postEvent() to ${targetOrigin}`, event);
  targetWindow.postMessage(event, targetOrigin);
}

function getParentTargetOrigin() {
  const targetOrigin = window.Disy.targetOrigin;
  if (targetOrigin) {
    return targetOrigin;
  } else {
    return location.origin;
  }
}

type RequestResponse<T extends IncomingCadenzaEventType> = T extends 'getData' ? Blob : never;

export async function handleRequest<T extends IncomingCadenzaEventType>(
  event: IncomingCadenzaEvent<T>,
  promise: Promise<void | RequestResponse<T>>,
) {
  const port = event.responsePort;
  assertNonNullable(port, 'The response port is required to handle a request');
  try {
    port.postMessage({
      type: `${event.type}:success`,
      detail: await promise,
    });
  } catch {
    port.postMessage({ type: `${event.type}:error` });
  }
}
